import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { fetchListeners, fetchVoiceListeners, Listener } from '../../api/listenersApi';
import { toApiError } from '../../api/http';
import { useApp } from '../../context/AppContext';
import VoiceCreationFlow from '../../components/VoiceCapture/VoiceCreationFlow';
import ListenerPersonalizationFlow from '../../components/VoiceCapture/ListenerPersonalizationFlow';
import VoiceRecordingWorkflow from '../../components/VoiceRecording/VoiceRecordingWorkflow';
import { useBedtimeContext } from '../../context/BedtimeContext';
import { features } from '../../config/features';
import { familyVoiceStatus } from '../../copy/terminology';
import { MicIcon, PlusIcon } from '../../components/icons';
import { EmptyState, Panel, PillButton, SettingsRow, StateMessage } from '../../components/ui';
import './Voice.css';

type Flow =
  | { kind: 'create'; voiceId?: string; voiceName?: string }
  | { kind: 'personalize'; voiceId: string; voiceName: string; listener: Listener }
  | null;

interface LegacyRouteState {
  invitedVoiceId?: string;
  invitedVoiceName?: string;
  invitedListenerId?: string;
  voiceId?: string;
  listenerId?: string;
}

type CoverageState = { listeners: Listener[]; error: boolean };

function isReadyVoice(voice: { status: string; consent_active?: boolean | null }) {
  return voice.status === 'ready' && voice.consent_active === true;
}

function activeListenersForVoice(voice: { household_id?: string | null }, listeners: Listener[]) {
  if (!voice.household_id) return [];
  return listeners.filter((listener) => (
    listener.state === 'active' && listener.household_id === voice.household_id
  ));
}

const Voice: React.FC = () => {
  const { state, fetchVoices } = useApp();
  const location = useLocation();
  const navigate = useNavigate();
  const { voiceId: routeVoiceId, listenerId: routeListenerId } = useParams();
  const bedtime = useBedtimeContext();
  const [listeners, setListeners] = useState<Listener[]>([]);
  const [coverage, setCoverage] = useState<Record<string, CoverageState>>({});
  const [flow, setFlow] = useState<Flow>(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState<ReturnType<typeof toApiError> | null>(null);
  const [pageRetryNonce, setPageRetryNonce] = useState(0);

  const isCreateRoute = location.pathname === '/voice/new';
  const isPersonalizeRoute = Boolean(routeVoiceId && routeListenerId);

  useEffect(() => {
    let active = true;
    setPageLoading(true);
    setPageError(null);
    Promise.all([fetchVoices(true), fetchListeners()])
      .then(([, listenerRows]) => {
        if (active) setListeners(listenerRows);
      })
      .catch((error) => {
        if (active) setPageError(toApiError(error));
      })
      .finally(() => {
        if (active) setPageLoading(false);
      });
    return () => { active = false; };
  }, [fetchVoices, pageRetryNonce]);

  useEffect(() => {
    if (pageLoading || pageError || !state.voices.length) return undefined;
    let active = true;
    const loadCoverage = async () => {
      const entries = await Promise.all(state.voices.map(async (voice) => {
        try {
          return [voice.voice_id, { listeners: await fetchVoiceListeners(voice.voice_id), error: false }] as const;
        } catch {
          return [voice.voice_id, { listeners: [], error: true }] as const;
        }
      }));
      if (active) setCoverage(Object.fromEntries(entries));
    };
    void loadCoverage();
    return () => { active = false; };
  }, [pageError, pageLoading, state.voices]);

  useEffect(() => {
    if (pageLoading || pageError || flow) return;
    const params = new URLSearchParams(location.search);
    const routeState = (location.state || null) as LegacyRouteState | null;

    if (isPersonalizeRoute) {
      const voice = state.voices.find((candidate) => candidate.voice_id === routeVoiceId);
      const listener = listeners.find((candidate) => candidate.listener_id === routeListenerId);
      if (voice && listener && isReadyVoice(voice) && activeListenersForVoice(voice, listeners).some((item) => item.listener_id === listener.listener_id)) {
        setFlow({ kind: 'personalize', voiceId: voice.voice_id, voiceName: voice.voice_name, listener });
      }
      return;
    }

    if (isCreateRoute || params.get('task') === 'create') {
      const existingVoiceId = params.get('voiceId') || undefined;
      const existingVoice = state.voices.find((voice) => voice.voice_id === existingVoiceId);
      setFlow({ kind: 'create', voiceId: existingVoiceId, voiceName: existingVoice?.voice_name || routeState?.invitedVoiceName });
      if (!isCreateRoute) navigate(`/voice/new${existingVoiceId ? `?voiceId=${encodeURIComponent(existingVoiceId)}` : ''}`, { replace: true });
      return;
    }

    const legacyVoiceId = routeState?.invitedVoiceId || routeState?.voiceId || params.get('voiceId') || bedtime.pair?.voiceId;
    const legacyListenerId = routeState?.invitedListenerId || routeState?.listenerId || params.get('listenerId') || bedtime.pair?.listenerId;
    if (params.get('task') === 'personalize' || (routeState?.invitedVoiceId && routeState?.invitedListenerId)) {
      const voice = state.voices.find((candidate) => candidate.voice_id === legacyVoiceId);
      const listener = listeners.find((candidate) => candidate.listener_id === legacyListenerId);
      if (voice && listener && isReadyVoice(voice) && activeListenersForVoice(voice, listeners).some((item) => item.listener_id === listener.listener_id)) {
        setFlow({ kind: 'personalize', voiceId: voice.voice_id, voiceName: voice.voice_name, listener });
        navigate(`/voice/${voice.voice_id}/personalize/${listener.listener_id}`, { replace: true });
      }
    }
  }, [bedtime.pair, flow, isCreateRoute, isPersonalizeRoute, listeners, location, navigate, pageError, pageLoading, routeListenerId, routeVoiceId, state.voices]);

  const completeFlow = () => {
    setFlow(null);
    void fetchVoices();
    void bedtime.refresh();
    navigate('/voice', { replace: true });
  };

  const voiceCoverage = useMemo(() => (voiceId?: string) => {
    if (!voiceId) return 'Listener personalization is available from voice details.';
    const voice = state.voices.find((candidate) => candidate.voice_id === voiceId);
    if (!voice) return 'Listener personalization is available from voice details.';
    const active = activeListenersForVoice(voice, listeners);
    if (!active.length) return 'No active listeners yet.';
    const result = coverage[voiceId];
    if (!result) return `${active.length} active listener${active.length === 1 ? '' : 's'} available`;
    if (result.error) return 'We couldn’t check personal messages.';
    const complete = result.listeners.filter((listener) => listener.bookend_complete).length;
    return `${complete} of ${active.length} listener${active.length === 1 ? '' : 's'} personalized`;
  }, [coverage, listeners, state.voices]);

  // Capture flows render inside the night theme in a centred column, the
  // same composition as the accountless donor journey.
  if (flow?.kind === 'create') {
    const onCancel = () => navigate('/voice');
    if (!features.voiceCaptureV2) return <div className="voice-capture voice-capture--legacy luma-legacy"><VoiceRecordingWorkflow existingVoiceId={flow.voiceId} existingNickname={flow.voiceName} onComplete={completeFlow} onCancel={onCancel} captureMode="voice" /></div>;
    return <div className="voice-capture"><VoiceCreationFlow existingVoiceId={flow.voiceId} existingVoiceName={flow.voiceName} onComplete={completeFlow} onCancel={onCancel} /></div>;
  }
  if (flow?.kind === 'personalize') {
    const onCancel = () => navigate('/voice');
    if (!features.voiceCaptureV2) return <div className="voice-capture voice-capture--legacy luma-legacy"><VoiceRecordingWorkflow existingVoiceId={flow.voiceId} existingNickname={flow.voiceName} initialListenerId={flow.listener.listener_id} initialListenerName={flow.listener.display_name} allowNewListeners={false} onComplete={completeFlow} onCancel={onCancel} captureMode="listener" /></div>;
    return <div className="voice-capture"><ListenerPersonalizationFlow voiceId={flow.voiceId} voiceName={flow.voiceName} listener={flow.listener} onComplete={completeFlow} onCancel={onCancel} /></div>;
  }

  if (pageLoading) return <div className="voice"><StateMessage kind="loading" title="Loading Family voices…" shape="form" headingLevel={1} /></div>;
  if (pageError) return <div className="voice"><StateMessage kind="error" error={pageError} title="Family voices are unavailable" headingLevel={1} retry={{ onClick: () => setPageRetryNonce((nonce) => nonce + 1) }} /></div>;
  if (isPersonalizeRoute) return <div className="voice"><StateMessage kind="blocked" title="These personal messages are unavailable" message="Return to Family voices and choose an authorized listener from the voice details." headingLevel={1} retry={{ label: 'Family voices', to: '/voice' }} /></div>;

  const hasVoices = state.voices.length > 0;

  return (
    <div className="voice">
      <div className="voice__head">
        <div className="voice__title">
          <h1>Family voices</h1>
          <p className="voice__subtitle">Saved voices for your family’s bedtime.</p>
        </div>
        <div className="voice__actions">
          {hasVoices && <PillButton variant="primary" to="/voice/new" icon={<PlusIcon size={20} />}>Add a family voice</PillButton>}
          <PillButton variant="secondary" to="/voice/invitations">Invite someone to record</PillButton>
        </div>
      </div>
      {!hasVoices && (
        <EmptyState headingLevel={2} title="No Family voices yet" message="Record a familiar voice, then add personal messages." action={{ label: 'Add a family voice', to: '/voice/new' }} id="voice-empty" />
      )}
      {hasVoices && (
        <section className="voice__inventory" aria-labelledby="voice-list-title">
          <h2 id="voice-list-title">Your family voices</h2>
          <Panel purpose="settings">
            <div className="voice__rows" data-testid="family-voice-inventory">
              {state.voices.map((voice) => (
                <SettingsRow
                  key={voice.voice_id}
                  label={voice.voice_name}
                  description={`${familyVoiceStatus(voice.status, voice.consent_active, voice.sample_count)} · ${voiceCoverage(voice.voice_id)}`}
                  icon={voice.profile_picture_url ? <img className="voice__portrait" src={voice.profile_picture_url} alt="" /> : <MicIcon size={20} />}
                  to={`/voice/${voice.voice_id}`}
                />
              ))}
            </div>
          </Panel>
        </section>
      )}
      <p className="voice__note">Invitations, consent, and listener moments each have their own step. Changing a voice never changes the bedtime pair by itself.</p>
    </div>
  );
};

export default Voice;
